import { useMemo, useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/**
 * Period filter with presets (This Month, This Quarter, This Year, Custom)
 * `onChange` receives `{ from: "YYYY-MM-DD"|null, to: "YYYY-MM-DD"|null, preset: string }`.
 *
 * `preset` starts as `"this_month"` by default; pass a different `initialPreset`
 * if the caller wants all-time ("all") as the default.
 */
const PRESETS = [
    { key: "this_month", label: "This Month" },
    { key: "this_quarter", label: "This Quarter" },
    { key: "this_year", label: "This Year" },
    { key: "all", label: "All Time" },
    { key: "custom", label: "Custom" },
];

function fmt(d) {
    if (!(d instanceof Date)) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function rangeFor(preset) {
    const now = new Date();
    if (preset === "this_month") {
        return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
                 to: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
    }
    if (preset === "this_quarter") {
        const q = Math.floor(now.getMonth() / 3);
        return { from: fmt(new Date(now.getFullYear(), q * 3, 1)),
                 to: fmt(new Date(now.getFullYear(), q * 3 + 3, 0)) };
    }
    if (preset === "this_year") {
        return { from: fmt(new Date(now.getFullYear(), 0, 1)),
                 to: fmt(new Date(now.getFullYear(), 11, 31)) };
    }
    // all or custom with no saved dates
    return { from: null, to: null };
}

export function PeriodFilter({ initialPreset = "this_month", onChange, compact = false }) {
    const [preset, setPreset] = useState(initialPreset);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");

    // Whenever the preset changes (and isn't custom), reset from/to to the preset's range.
    useEffect(() => {
        if (preset === "custom") return;
        const r = rangeFor(preset);
        setFrom(r.from || "");
        setTo(r.to || "");
    }, [preset]);

    // Bubble every change upward.
    useEffect(() => {
        onChange?.({ from: from || null, to: to || null, preset });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, preset]);

    const rangeText = useMemo(() => {
        if (preset === "all") return "All time";
        if (!from && !to) return "—";
        return `${from || "…"} → ${to || "…"}`;
    }, [from, to, preset]);

    return (
        <div className="flex flex-wrap items-center gap-2" data-testid="period-filter">
            <div className="flex rounded-sm border border-slate-200 overflow-hidden">
                {PRESETS.map((p) => (
                    <button
                        key={p.key}
                        onClick={() => setPreset(p.key)}
                        className={`px-2.5 py-1.5 text-[11.5px] transition-colors
                            ${preset === p.key ? "bg-[#002FA7] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                        data-testid={`period-preset-${p.key}`}
                        type="button"
                    >
                        {p.label}
                    </button>
                ))}
            </div>
            {preset === "custom" && (
                <>
                    <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm h-8 w-36 font-mono text-[12px]" data-testid="period-from"/>
                    <span className="text-[11px] text-slate-500">to</span>
                    <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm h-8 w-36 font-mono text-[12px]" data-testid="period-to"/>
                </>
            )}
            {!compact && (
                <span className="text-[10.5px] uppercase tracking-widest text-slate-400 ml-1" data-testid="period-label">{rangeText}</span>
            )}
        </div>
    );
}

/** Build axios `params` additions from a PeriodFilter state. */
export function periodParams(p) {
    if (!p) return {};
    const out = {};
    if (p.from) out.from = p.from;
    if (p.to) out.to = p.to;
    return out;
}
